import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Input, Select, Space, Table, Tabs, Tooltip, Typography } from 'antd';
import type { ColumnsType, SorterResult } from 'antd/es/table/interface';
import { PlusOutlined, ReloadOutlined, SwapOutlined, UserSwitchOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import {
  Pestania,
  consultarOportunidades,
  leerCatalogos,
  leerYo,
} from '../api/oportunidadesApi';
import type { Catalogos, EmpleadoUsuario, ListadoPagina, OportunidadFila, OrdenListado } from '../api/oportunidadesApi';
import { EstadoOportunidadTag, ResponsableOportunidad } from '../components/Oportunidades/EstadoOportunidadTag';
import { DetalleDrawer } from '../components/Oportunidades/DetalleDrawer';
import { AsignarModal, EstadoModal, NuevaOportunidadModal } from '../components/Oportunidades/Modales';
import { errorOportunidad } from '../components/Oportunidades/errores';
import { ESTADOS_POR_DEFECTO, NOMBRE_PESTANIA, PENDIENTES_ASIGNAR_POR_DEFECTO, etiquetaEstado, recortar } from '../components/Oportunidades/reglas';
import GcPagina from '../components/Layout/GcPagina';
import { FctGlifoAtencion, FctTarjeta, fmtFecha, fmtNumero } from '../fct/react';
import { fct } from '../fct/tokens/tokens';
import './OportunidadesPage.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/** Filtros, página y orden de una pestaña (cada pestaña conserva los suyos, R-03). */
interface EstadoPestania {
  estados: number[];
  idResponsable?: number;
  soloPendientes: boolean;
  fechas?: [Dayjs, Dayjs] | null;
  idIniciativa?: number;
  idOrigen?: number;
  texto: string;
  orden: OrdenListado;
  ordenDescendente: boolean;
  pagina: number;
  tamanio: number;
}

// Filtro inicial (R-03, spec rev. 3): el uso más habitual. No hay botón para volver a él.
const INICIAL: EstadoPestania = {
  estados: ESTADOS_POR_DEFECTO,
  soloPendientes: PENDIENTES_ASIGNAR_POR_DEFECTO,
  texto: '',
  orden: 'FECHA',
  ordenDescendente: true,
  pagina: 1,
  tamanio: 50,
};

const PESTANIAS = [Pestania.Venta, Pestania.Compra, Pestania.Solicitud];

const COLUMNA_ORDEN: Record<string, OrdenListado> = {
  idOportunidad: 'NUMERO',
  fecha: 'FECHA',
  idEstado: 'ESTADO',
  responsable: 'RESPONSABLE',
};

/**
 * Aviso de pendientes de asignar en la pestaña (R-04, P-09; spec rev. 3): solo un icono, sin cifra.
 * El número lo da el listado, que al entrar ya filtra las pendientes de asignar.
 */
const AvisoPendientes: React.FC<{ n?: number }> = ({ n }) =>
  n ? (
    <Tooltip title="Hay oportunidades vivas pendientes de asignar">
      {/* Glifo de atención del manual (R-14): el nivel «atención» lleva su tinta; nada de color suelto. */}
      <span
        role="img"
        aria-label="Hay oportunidades pendientes de asignar"
        style={{ marginLeft: 6 }}
      >
        {/* El glifo trae su propio aria-label genérico («Atención»): se oculta y manda el del envoltorio. */}
        <span aria-hidden="true">
          <FctGlifoAtencion nivel="atencion" />
        </span>
      </span>
    </Tooltip>
  ) : null;

/**
 * Oportunidades (OPOR-I001): herramienta de Marketing para ver, dar de alta, asignar, cambiar de
 * estado y editar las oportunidades de venta, de compra y las solicitudes de información.
 * Todo el filtrado, la ordenación y la paginación son de servidor (≈11.500 ventas y ≈24.000
 * compras y solicitudes). Recurso ACL: Oportunidades.Gestionar (ruta y API).
 */
const OportunidadesPage: React.FC = () => {
  const [catalogos, setCatalogos] = useState<Catalogos | null>(null);
  const [yo, setYo] = useState<EmpleadoUsuario | null>(null);
  const [pestania, setPestania] = useState<Pestania>(Pestania.Venta);
  const [filtros, setFiltros] = useState<Record<Pestania, EstadoPestania>>({
    [Pestania.Venta]: INICIAL, [Pestania.Compra]: INICIAL, [Pestania.Solicitud]: INICIAL,
  });
  const [textoEscrito, setTextoEscrito] = useState('');
  const [datos, setDatos] = useState<ListadoPagina | null>(null);
  // Pendientes de asignar por pestaña, de la última respuesta recibida. No se vacían mientras carga
  // el listado ni al cambiar de pestaña, para que los avisos no parpadeen (spec rev. 3).
  const [pendientes, setPendientes] = useState<Record<Pestania, number> | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [nuevaAbierta, setNuevaAbierta] = useState(false);
  const [asignarObjetivo, setAsignarObjetivo] = useState<{ pestania: Pestania; id: number; idResponsable?: number } | null>(null);
  const [estadoObjetivo, setEstadoObjetivo] = useState<{ pestania: Pestania; id: number; idEstado: number } | null>(null);
  const [detalleObjetivo, setDetalleObjetivo] = useState<{ pestania: Pestania; id: number } | null>(null);
  const [versionDetalle, setVersionDetalle] = useState(0);

  const f = filtros[pestania];
  const cambiar = (cambios: Partial<EstadoPestania>, volverAPrimera = true) =>
    setFiltros((actual) => ({
      ...actual,
      [pestania]: { ...actual[pestania], ...cambios, ...(volverAPrimera ? { pagina: 1 } : {}) },
    }));

  useEffect(() => {
    leerCatalogos().then(setCatalogos).catch((e) => setError(errorOportunidad(e).mensaje));
    leerYo().then(setYo).catch(() => setYo(null));
  }, []);

  // ?venta=N o ?compra=N abre el detalle de esa oportunidad: así enlaza «Acciones» de Ferias (M6 §3.2).
  const [busqueda] = useSearchParams();
  useEffect(() => {
    const venta = Number(busqueda.get('venta'));
    const compra = Number(busqueda.get('compra'));
    if (Number.isInteger(venta) && venta > 0) {
      setPestania(Pestania.Venta);
      setDetalleObjetivo({ pestania: Pestania.Venta, id: venta });
    } else if (Number.isInteger(compra) && compra > 0) {
      setPestania(Pestania.Compra);
      setDetalleObjetivo({ pestania: Pestania.Compra, id: compra });
    }
  }, [busqueda]);

  useEffect(() => setTextoEscrito(filtros[pestania].texto), [pestania]); // eslint-disable-line react-hooks/exhaustive-deps

  // Solo vale la respuesta de la última petición: una lenta de otra pestaña no puede pintar filas
  // bajo la pestaña equivocada (revisión independiente, hallazgo 1).
  const ultimaPeticion = useRef(0);

  const cargar = useCallback(async () => {
    const n = ++ultimaPeticion.current;
    setCargando(true);
    setError(null);
    try {
      const pagina = await consultarOportunidades({
        pestania,
        estados: f.estados.length ? f.estados : undefined,
        idResponsable: f.idResponsable,
        soloPendientesAsignar: f.soloPendientes,
        desde: f.fechas?.[0]?.format('YYYY-MM-DD'),
        hasta: f.fechas?.[1]?.format('YYYY-MM-DD'),
        idIniciativa: f.idIniciativa,
        idOrigen: f.idOrigen,
        texto: f.texto || undefined,
        orden: f.orden,
        ordenDescendente: f.ordenDescendente,
        pagina: f.pagina,
        tamanio: f.tamanio,
      });
      if (n === ultimaPeticion.current) {
        setDatos(pagina);
        setPendientes({
          [Pestania.Venta]: pagina.pendientesVenta,
          [Pestania.Compra]: pagina.pendientesCompra,
          [Pestania.Solicitud]: pagina.pendientesSolicitud,
        } as Record<Pestania, number>);
      }
    } catch (e) {
      // CA-18: error con reintento, sin perder los filtros (y sin dejar filas de otra consulta).
      if (n === ultimaPeticion.current) {
        setDatos(null);
        setError(errorOportunidad(e).mensaje);
      }
    } finally {
      if (n === ultimaPeticion.current) setCargando(false);
    }
  }, [pestania, f]);

  useEffect(() => { cargar(); }, [cargar]);

  const trasAccion = () => {
    setAsignarObjetivo(null);
    setEstadoObjetivo(null);
    setVersionDetalle((v) => v + 1);
    cargar();
  };

  const empleadosVisibles = useMemo(
    () => (catalogos?.empleados ?? []).map((e) => ({ value: e.id, label: e.asignable ? e.nombre : `${e.nombre} (no asignable)` })),
    [catalogos],
  );

  const orden = (campo: string) =>
    COLUMNA_ORDEN[campo] === f.orden ? (f.ordenDescendente ? 'descend' as const : 'ascend' as const) : null;

  const columnas: ColumnsType<OportunidadFila> = useMemo(() => {
    const cols: ColumnsType<OportunidadFila> = [
      {
        title: 'Nº', dataIndex: 'idOportunidad', key: 'idOportunidad', width: 90, align: 'right', className: 'fct-num', sorter: true,
        sortOrder: orden('idOportunidad'),
      },
      {
        title: pestania === Pestania.Venta ? 'Generación' : 'Solicitud', dataIndex: 'fecha', key: 'fecha', width: 110, align: 'right', className: 'fct-num', sorter: true,
        sortOrder: orden('fecha'),
        render: (v?: string) => (v ? fmtFecha(v) : '—'),
      },
    ];
    if (pestania === Pestania.Venta) {
      cols.push(
        {
          title: 'Farmacia', key: 'farmacia', width: 220,
          render: (_, r) => (r.idFarmacia
            ? <><div>{r.farmacia}</div></>
            : <Text type="secondary">Sin identificar</Text>),
        },
        { title: 'Titulares', dataIndex: 'titulares', key: 'titulares', width: 190, render: (v?: string) => v || <Text type="secondary">—</Text> },
      );
    } else {
      cols.push({ title: 'Cliente', dataIndex: 'cliente', key: 'cliente', width: 200 });
    }
    cols.push({
      title: 'Iniciativa · origen · detalle', key: 'origen', width: 220,
      render: (_, r) => (
        <>
          <div>{r.origen}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{[r.iniciativa, r.detalle].filter(Boolean).join(' · ')}</Text>
        </>
      ),
    });
    if (pestania === Pestania.Venta) {
      cols.push({ title: 'Tipo', dataIndex: 'tipoOperacion', key: 'tipoOperacion', width: 90 });
    }
    if (pestania === Pestania.Solicitud) {
      cols.push({ title: 'Operaciones', dataIndex: 'operaciones', key: 'operaciones', width: 240, render: (v?: string) => v || '—' });
    }
    cols.push(
      {
        // 220: la pastilla «Descarte pendiente de confirmar» (R-02 bis) no cabe en menos y no se parte.
        title: 'Estado', dataIndex: 'idEstado', key: 'idEstado', width: 220, sorter: true, sortOrder: orden('idEstado'),
        render: (v: number) => <EstadoOportunidadTag estado={v} />,
      },
      {
        title: 'Responsable', dataIndex: 'responsable', key: 'responsable', width: 190, sorter: true, sortOrder: orden('responsable'),
        render: (v: string | undefined, r) => (
          <ResponsableOportunidad responsable={v} pendienteAsignar={r.pendienteAsignar} responsableBaja={r.responsableBaja} />
        ),
      },
      {
        title: 'Observaciones', dataIndex: 'observaciones', key: 'observaciones', width: 260,
        // R-02: recortadas en la columna, completas en el tooltip.
        render: (v?: string) => (v
          ? <Tooltip title={<div style={{ whiteSpace: 'pre-wrap', maxHeight: 320, overflow: 'auto' }}>{v}</div>} overlayStyle={{ maxWidth: 480 }}>
              <span>{recortar(v)}</span>
            </Tooltip>
          : <Text type="secondary">—</Text>),
      },
      {
        title: '', key: 'acciones', width: 90, fixed: 'right',
        render: (_, r) => (
          <Space size={0} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Asignar">
              <Button type="text" aria-label="Asignar" icon={<UserSwitchOutlined />}
                onClick={() => setAsignarObjetivo({ pestania: r.pestania, id: r.idOportunidad, idResponsable: r.idResponsable })} />
            </Tooltip>
            <Tooltip title="Cambiar estado">
              <Button type="text" aria-label="Cambiar estado" icon={<SwapOutlined />}
                onClick={() => setEstadoObjetivo({ pestania: r.pestania, id: r.idOportunidad, idEstado: r.idEstado })} />
            </Tooltip>
          </Space>
        ),
      },
    );
    return cols;
  }, [pestania, f.orden, f.ordenDescendente]); // eslint-disable-line react-hooks/exhaustive-deps

  const contador = (p: Pestania) => pendientes?.[p];

  return (
    <GcPagina
      seccion="Oportunidades"
      titulo="Oportunidades"
      contexto="Marketing · Gestión comercial"
      densidad="compacta"
      acciones={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setNuevaAbierta(true)} disabled={!catalogos}>
          Nueva oportunidad
        </Button>
      }
    >
      <FctTarjeta>
        <Tabs
          activeKey={String(pestania)}
          onChange={(k) => { setDatos(null); setPestania(Number(k) as Pestania); }}
          items={PESTANIAS.map((p) => ({
            key: String(p),
            label: <span>{NOMBRE_PESTANIA[p]}<AvisoPendientes n={contador(p)} /></span>,
          }))}
        />

        <Space wrap size={[12, 12]} style={{ marginBottom: 12 }}>
          <Select
            mode="multiple"
            aria-label="Estado"
            placeholder="Todos los estados"
            style={{ minWidth: 260 }}
            value={f.estados}
            onChange={(v) => cambiar({ estados: v })}
            options={[1, 2, 3, 4].map((e) => ({ value: e, label: etiquetaEstado(e) }))}
            maxTagCount="responsive"
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            aria-label="Responsable"
            placeholder="Responsable"
            style={{ width: 240 }}
            value={f.idResponsable}
            onChange={(v) => cambiar({ idResponsable: v })}
            options={empleadosVisibles}
          />
          <RangePicker
            format="DD/MM/YYYY"
            value={f.fechas ?? null}
            onChange={(v) => cambiar({ fechas: v as [Dayjs, Dayjs] | null })}
            aria-label="Fechas"
          />
          <Select
            allowClear
            aria-label="Iniciativa"
            placeholder="Iniciativa"
            style={{ width: 150 }}
            value={f.idIniciativa}
            onChange={(v) => cambiar({ idIniciativa: v })}
            options={(catalogos?.iniciativas ?? []).map((i) => ({ value: i.id, label: i.descripcion }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            aria-label="Origen"
            placeholder="Origen"
            style={{ width: 220 }}
            value={f.idOrigen}
            onChange={(v) => cambiar({ idOrigen: v })}
            options={(catalogos?.origenes ?? []).map((o) => ({ value: o.id, label: o.descripcion }))}
          />
          <Checkbox checked={f.soloPendientes} onChange={(e) => cambiar({ soloPendientes: e.target.checked })}>
            Pendientes de asignar
          </Checkbox>
          <Input.Search
            allowClear
            aria-label="Buscar"
            placeholder={pestania === Pestania.Venta ? 'Nº, farmacia o titular' : 'Nº, cliente o farmacia'}
            style={{ width: 260 }}
            value={textoEscrito}
            onChange={(e) => setTextoEscrito(e.target.value)}
            onSearch={(v) => cambiar({ texto: v.trim() })}
          />
        </Space>

        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            action={<Button size="small" icon={<ReloadOutlined />} onClick={cargar}>Reintentar</Button>}
            style={{ marginBottom: 12 }}
          />
        )}

        <Table<OportunidadFila>
          rowKey="idOportunidad"
          size="small"
          loading={cargando}
          columns={columnas}
          dataSource={datos?.filas ?? []}
          scroll={{ x: 1500 }}
          locale={{ emptyText: 'No hay oportunidades con estos filtros' }}
          rowClassName={(r) => (r.pendienteAsignar ? 'opor-fila-pendiente' : '')}
          onRow={(r) => ({
            style: { cursor: 'pointer' },
            // La pestaña de la FILA (la devuelve el servidor), no la activa: nunca se actúa sobre otro tipo.
            onClick: () => setDetalleObjetivo({ pestania: r.pestania, id: r.idOportunidad }),
          })}
          onChange={(pag, _filtros, sorter) => {
            const s = sorter as SorterResult<OportunidadFila>;
            const campo = s.order ? COLUMNA_ORDEN[String(s.columnKey)] : undefined;
            cambiar({
              pagina: pag.current ?? 1,
              tamanio: pag.pageSize ?? f.tamanio,
              orden: campo ?? 'FECHA',
              ordenDescendente: campo ? s.order === 'descend' : true,
            }, false);
          }}
          pagination={{
            current: f.pagina,
            pageSize: f.tamanio,
            total: datos?.totalFiltrado ?? 0,
            pageSizeOptions: ['20', '50', '100', '200'],
            showSizeChanger: true,
            showTotal: (t) => `${fmtNumero(t)} oportunidades`,
          }}
          style={{ background: fct.superficie.tarjeta }}
        />
      </FctTarjeta>

      {catalogos && (
        <>
          <NuevaOportunidadModal
            abierto={nuevaAbierta}
            pestaniaInicial={pestania}
            catalogos={catalogos}
            yo={yo}
            onCerrar={() => setNuevaAbierta(false)}
            onCreada={(p, id) => {
              setNuevaAbierta(false);
              // Si cambia de pestaña, la carga la dispara el propio cambio; si no, se pide aquí.
              if (p === pestania) cargar(); else { setDatos(null); setPestania(p); }
              setDetalleObjetivo({ pestania: p, id });
            }}
          />
          <AsignarModal objetivo={asignarObjetivo} catalogos={catalogos} yo={yo} onCerrar={() => setAsignarObjetivo(null)} onHecho={trasAccion} />
          <EstadoModal objetivo={estadoObjetivo} yo={yo} onCerrar={() => setEstadoObjetivo(null)} onHecho={trasAccion} />
          <DetalleDrawer
            objetivo={detalleObjetivo}
            catalogos={catalogos}
            yo={yo}
            version={versionDetalle}
            onCerrar={() => setDetalleObjetivo(null)}
            onAsignar={(p, id, idResponsable) => setAsignarObjetivo({ pestania: p, id, idResponsable })}
            onCambiarEstado={(p, id, idEstado) => setEstadoObjetivo({ pestania: p, id, idEstado })}
            onCambiado={cargar}
          />
        </>
      )}
    </GcPagina>
  );
};

export default OportunidadesPage;
